import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  ApiError,
  listIntakes,
  readSession,
  type IntakeListSortField,
  type IntakeListSortDirection,
  type IntakeSummary,
  type ListIntakesResponse,
} from '../../lib/api.js';

// D2.5.1 + D3.2.1 — Admin dashboard for in-flight tenant intakes.
//
// Fetches `GET /api/platform/intakes` and renders a filterable/sortable
// table grouped by state. Each row links to the detail page. % complete
// is `(lastStep / 10) * 100` per the plan's step D2.5.1 spec.
//
// D3.2.1 additions:
//   - "Needs Review" quick-filter chip — one click sets
//     state=submitted + sortBy=submittedAt + sortDir=asc (FIFO queue).
//   - Pending-review count badge — a second lightweight query counts
//     `state=submitted` so the badge reflects queue depth even when
//     the main table is filtered to a different state.

type IntakeListProps = {
  config: ClientConfig;
};

const STATE_FILTER_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: '', label: 'All states' },
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'provisioned', label: 'Provisioned' },
];

const SORT_OPTIONS: ReadonlyArray<{
  value: IntakeListSortField;
  label: string;
}> = [
  { value: 'updatedAt', label: 'Last activity' },
  { value: 'createdAt', label: 'Invited' },
  { value: 'submittedAt', label: 'Submitted' },
  { value: 'state', label: 'State' },
];

function percentComplete(lastStep: number): number {
  const n = Math.max(1, Math.min(10, Math.round(lastStep)));
  return n * 10;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatState(s: string): string {
  return s
    .split('_')
    .map((p) => (p ? p[0]!.toUpperCase() + p.slice(1) : ''))
    .join(' ');
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'empty_session' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: ListIntakesResponse };

function isNeedsReviewActive(
  stateFilter: string,
  sortBy: IntakeListSortField,
  sortDir: IntakeListSortDirection,
): boolean {
  return (
    stateFilter === 'submitted' &&
    sortBy === 'submittedAt' &&
    sortDir === 'asc'
  );
}

export function IntakeList({ config }: IntakeListProps): JSX.Element {
  const [view, setView] = useState<ViewState>({ kind: 'loading' });
  const [stateFilter, setStateFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<IntakeListSortField>('updatedAt');
  const [sortDir, setSortDir] = useState<IntakeListSortDirection>('desc');
  const [pendingReviewCount, setPendingReviewCount] = useState<number | null>(
    null,
  );

  const query = useMemo(
    () => ({
      state: stateFilter || undefined,
      sortBy,
      sortDir,
    }),
    [stateFilter, sortBy, sortDir],
  );

  const needsReviewActive = isNeedsReviewActive(stateFilter, sortBy, sortDir);

  useEffect(() => {
    const { token } = readSession();
    if (!token) {
      setView({ kind: 'empty_session' });
      return;
    }
    let cancelled = false;
    setView({ kind: 'loading' });
    listIntakes(config.deployment.apiUrl, token, query)
      .then((data) => {
        if (cancelled) return;
        setView({ kind: 'ready', data });
        // When the current filter happens to be `state=submitted`, the
        // main response's `total` is already the pending-review count —
        // reuse it instead of firing a second request.
        if (query.state === 'submitted') {
          setPendingReviewCount(data.total);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? `Could not load intakes: ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Unexpected error';
        setView({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [config.deployment.apiUrl, query]);

  // D3.2.1 — Independently fetch the pending-review count so the badge
  // stays accurate even when the main table is filtered to a different
  // state. `limit: 1` keeps the payload small; we only consume `total`.
  useEffect(() => {
    if (query.state === 'submitted') return; // reuses the primary fetch above
    const { token } = readSession();
    if (!token) return;
    let cancelled = false;
    listIntakes(config.deployment.apiUrl, token, {
      state: 'submitted',
      limit: 1,
    })
      .then((data) => {
        if (cancelled) return;
        setPendingReviewCount(data.total);
      })
      .catch(() => {
        // Non-fatal — the badge just stays hidden if the count read
        // fails. The main list error handler already surfaces list
        // failures; we don't want two alerts for one network blip.
      });
    return () => {
      cancelled = true;
    };
  }, [config.deployment.apiUrl, query.state]);

  const intakes =
    view.kind === 'ready' ? view.data.intakes : ([] as IntakeSummary[]);
  const total = view.kind === 'ready' ? view.data.total : 0;

  function toggleNeedsReview(): void {
    if (needsReviewActive) {
      // Second click clears the quick-filter back to defaults so Johnny
      // can see the rest of the pipeline again.
      setStateFilter('');
      setSortBy('updatedAt');
      setSortDir('desc');
      return;
    }
    setStateFilter('submitted');
    setSortBy('submittedAt');
    setSortDir('asc');
  }

  return (
    <section className="ferro-admin-page">
      <header className="ferro-admin-page__header">
        <h1 className="ferro-section__title">In-flight intakes</h1>
        <p className="ferro-muted">
          Every tenant intake grouped by state. Click a row for the full
          triage view.
        </p>
      </header>

      <div
        className="ferro-quick-filters"
        role="region"
        aria-label="Quick filters"
        data-testid="intake-list-quick-filters"
      >
        <button
          type="button"
          className={
            needsReviewActive
              ? 'ferro-btn ferro-btn--primary'
              : 'ferro-btn ferro-btn--ghost'
          }
          aria-pressed={needsReviewActive}
          onClick={toggleNeedsReview}
          data-testid="intake-list-needs-review-toggle"
        >
          {needsReviewActive ? 'Clear needs-review view' : 'Needs review'}
          {pendingReviewCount !== null && pendingReviewCount > 0 ? (
            <span
              className="ferro-count-badge"
              data-testid="intake-list-needs-review-badge"
              aria-label={
                pendingReviewCount === 1
                  ? '1 intake waiting for review'
                  : `${pendingReviewCount} intakes waiting for review`
              }
            >
              {pendingReviewCount}
            </span>
          ) : null}
        </button>
      </div>

      <div
        className="ferro-form"
        role="region"
        aria-label="Intake filters"
        data-testid="intake-list-filters"
      >
        <label className="ferro-field">
          <span className="ferro-field__label">State</span>
          <select
            className="ferro-field__input"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            data-testid="intake-list-state-filter"
          >
            {STATE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Sort by</span>
          <select
            className="ferro-field__input"
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as IntakeListSortField)
            }
            data-testid="intake-list-sort-by"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Direction</span>
          <select
            className="ferro-field__input"
            value={sortDir}
            onChange={(e) =>
              setSortDir(e.target.value as IntakeListSortDirection)
            }
            data-testid="intake-list-sort-dir"
          >
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </label>
      </div>

      {view.kind === 'loading' ? (
        <p className="ferro-muted" role="status" aria-live="polite">
          Loading intakes…
        </p>
      ) : null}

      {view.kind === 'empty_session' ? (
        <p className="ferro-form__error" role="alert">
          Your session has expired. Please sign in again.
        </p>
      ) : null}

      {view.kind === 'error' ? (
        <p className="ferro-form__error" role="alert">
          {view.message}
        </p>
      ) : null}

      {view.kind === 'ready' ? (
        <>
          <p
            className="ferro-muted"
            data-testid="intake-list-total"
            aria-live="polite"
          >
            {total === 0
              ? 'No intakes match the current filters.'
              : total === 1
                ? '1 intake total'
                : `${total} intakes total`}
          </p>

          {intakes.length === 0 ? null : (
            <ul
              className="ferro-table"
              aria-label="Tenant intakes"
              data-testid="intake-list-rows"
            >
              {intakes.map((i) => (
                <IntakeRow key={i.id} intake={i} />
              ))}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}

function IntakeRow({ intake }: { intake: IntakeSummary }): JSX.Element {
  const pct = percentComplete(intake.lastStep);
  return (
    <li
      className="ferro-table__row"
      data-testid={`intake-list-row-${intake.id}`}
    >
      <Link
        to={`/platform/intake/${intake.id}`}
        className="ferro-table__link"
      >
        <span className="ferro-table__primary">
          {intake.companyName ?? intake.inviteEmail}
        </span>
        <span className="ferro-table__meta">
          <span data-testid={`intake-list-email-${intake.id}`}>
            {intake.inviteEmail}
          </span>
          {' · '}
          <span data-testid={`intake-list-state-${intake.id}`}>
            {formatState(intake.state)}
          </span>
          {' · '}
          <span>
            Step {intake.lastStep} of 10 ({pct}%)
          </span>
          {' · '}
          <span>Last activity {formatDate(intake.updatedAt)}</span>
          {intake.nudgeCount > 0 ? (
            <>
              {' · '}
              <span data-testid={`intake-list-nudges-${intake.id}`}>
                {intake.nudgeCount} nudge
                {intake.nudgeCount === 1 ? '' : 's'}
              </span>
            </>
          ) : null}
        </span>
        <span className="ferro-table__badge">{formatState(intake.state)}</span>
      </Link>
    </li>
  );
}
